import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Select, message } from 'antd';
import { AddTutorsToOrganizationByEmail } from '../../helper/apiHelper';
import { validateMultiEmail } from '../../Validators/ValidatorEmail';
import type { JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { EMPTY_ARRAY } from '../../Constants/constants';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useTutorsOfOrganization } from '../../hooks/useTutors';
import { TutorsOfOrganizationsTable } from './TutorsOfOrganizationsTable';
import { useUserOrganizations } from '../../hooks/useOrganizations';

const TutorManagementComponent = (): JSX.Element => {
  const loggedInUser = useCurrentUser();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const { data: userOrganizations } = useUserOrganizations(loggedInUser.id);
  const selectedOrganizationId = Form.useWatch('organization', form);
  const { data: tutors = EMPTY_ARRAY } = useTutorsOfOrganization(
    selectedOrganizationId
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (userOrganizations?.length === 1) {
      form.setFieldValue('organization', userOrganizations[0].id);
    }
  }, [form, userOrganizations]);

  const addTutorsMutation = useMutation({
    mutationFn: ({
      organizationId,
      emails,
    }: {
      organizationId: number;
      emails: string[];
    }) => AddTutorsToOrganizationByEmail(organizationId, emails),
    onSuccess: () => {
      messageApi.success(t('tutorManComp.success'));
      queryClient.invalidateQueries({
        queryKey: [
          ...QUERY_KEYS.TUTORS_OF_ORGANIZATIONS,
          Number(selectedOrganizationId),
        ],
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ORGANIZATIONS_TUTORS,
      });
      form.resetFields();
    },
    onError: () => {
      messageApi.error(t('tutorManComp.error'));
    },
  });

  const onAdd = async (): Promise<void> => {
    const organizationId = form.getFieldValue('organization');
    const emails = form.getFieldValue('newTutorEmail') ?? [];

    addTutorsMutation.mutate({ organizationId, emails });
  };

  return (
    <>
      {contextHolder}
      <Form form={form} size="large">
        <Form.Item name="organization">
          <Select
            size="large"
            placeholder={t('tutorManComp.selOrg')}
            options={userOrganizations?.map((org) => ({
              label: org.name,
              value: org.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="newTutorEmail"
          rules={[{ validator: validateMultiEmail }]}
        >
          <Select
            size="large"
            mode="tags"
            placeholder={t('tutorManComp.typeEmailForTutor')}
            options={tutors.map((tutor) => ({
              label: `${tutor.firstName} ${tutor.lastName}`.trim(),
              value: tutor.email,
            }))}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" onClick={onAdd}>
            {t('tutorManComp.addToOrg')}
          </Button>
        </Form.Item>
      </Form>

      <TutorsOfOrganizationsTable />
    </>
  );
};

export default TutorManagementComponent;
